// Firebase web SDK initialization for the cnapractice.com SPA.
//
// Note: this config object is PUBLIC — it ships in every deployed Firebase
// web app. Real security comes from Firestore Security Rules, App Check, and
// API key restrictions in the GCP Console (set referrer to cnapractice.com
// before public launch).
//
// In dev, set VITE_USE_EMULATORS=true and run `firebase emulators:start` —
// this file will rewire all SDKs to localhost ports defined in firebase.json.

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import type { FirebaseStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: 'AIzaSyANNqU3q-feOdhS5XPgcTuhwC5fyzRkMlA',
  authDomain: 'practicecom-9f7b3.firebaseapp.com',
  projectId: 'practicecom-9f7b3',
  storageBucket: 'practicecom-9f7b3.firebasestorage.app',
  messagingSenderId: '1066289978924',
  appId: '1:1066289978924:web:0577ddd0b3661519707b89',
  measurementId: 'G-GF9G9PN0DY',
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
// Persistent local cache: on repeat visits the profile document (and every
// other listened document) renders from IndexedDB immediately while the server
// snapshot follows, so the signed-in gate no longer waits on a network round
// trip. Falls back to the in-memory cache where IndexedDB is unavailable.
function createFirestore(): Firestore {
  try {
    return initializeFirestore(app, { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) });
  } catch (err) {
    console.warn('[firebase] persistent cache unavailable; using memory cache', err);
    return getFirestore(app);
  }
}
export const db = createFirestore();
export const functions = getFunctions(app, 'us-central1');

// Lazy storage — only the two admin panes (ContentBankSwarm, ExamReplay) use
// it. Loading `firebase/storage` synchronously here pulled ~50 kB into the
// main firebase-vendor chunk that 99% of users never need. Now those panes
// call `await getStorageLazy()` and pay the cost only when they open.
let storageInstance: FirebaseStorage | undefined;
export async function getStorageLazy(): Promise<FirebaseStorage> {
  if (storageInstance) return storageInstance;
  const { getStorage, connectStorageEmulator } = await import('firebase/storage');
  storageInstance = getStorage(app);
  if (import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectStorageEmulator(storageInstance, '127.0.0.1', 9199);
  }
  return storageInstance;
}

if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  console.info('[firebase] connected to local emulators');
}
